import type {
  ColumnDataSchemaModel,
  EditCell,
  EditorBase,
  EditorCtrConstructible,
  HyperFunc,
  VNode,
} from '@revolist/revogrid';

export interface RevoGridBeforeKeydownEvent {
  detail: {
    original: KeyboardEvent;
  };
  preventDefault(): void;
}

export function allowRevoGridFilterInputTyping(
  event: RevoGridBeforeKeydownEvent,
): void {
  const target = event.detail.original.target;

  if (target instanceof Element && target.closest('input.filter-box')) {
    event.preventDefault();
  }
}

export function selectCellTemplate(
  label: (model: any) => string,
  disabled: (model: any) => boolean,
  buttonLabel: string,
): (h: HyperFunc<VNode>, props: ColumnDataSchemaModel) => VNode {
  return (h, props) =>
    h(
      'div',
      { class: 'revo-cell-with-icon' },
      [h('span', { class: 'revo-cell-text' }, label(props.model)),
      h(
        'button',
        {
          type: 'button',
          class: 'revo-cell-select-btn',
          disabled: disabled(props.model),
          'aria-label': buttonLabel,
          'aria-haspopup': 'listbox',
          onMouseDown: (event: MouseEvent) => event.stopPropagation(),
          onDblClick: (event: MouseEvent) => event.stopPropagation(),
          onClick: async (event: MouseEvent) => {
            event.stopPropagation();
            if (disabled(props.model)) return;
            const button = event.currentTarget as HTMLElement;
            const grid = button.closest('revo-grid');
            if (!grid) return;
            await grid.clearFocus();
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            await grid.setCellEdit(props.rowIndex, props.prop, props.type);
          },
        },
        h(
          'svg',
          { width: '16', height: '16', viewBox: '0 0 20 20', fill: 'none', 'aria-hidden': 'true' },
          h('path', { d: 'm5 7.5 5 5 5-5', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }),
        ),
      )],
    );
}

/**
 * A date picker; a value that is not a date (pasted text kept for the user to fix) is shown as text, and is
 * edited with the grid's text editor.
 */
/** dd/mm/yyyy, the same day-first form a paste is read in, so copy → paste round-trips. */
function displayDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

/** A stored date as the cell shows it (dd/mm/yyyy); null when the value isn't a date. */
export function dateDisplayText(value: unknown): string | null {
  const iso = dateInputValue(value);
  return iso ? displayDate(iso) : null;
}

/**
 * The date as plain text. The cell is a normal grid cell (no `<input>` or button on top of it), so clicking
 * selects it and Ctrl+V / typing go through the grid like any other column, where the text is parsed. Enter or
 * double-click opens `createDateEditor`. Text that isn't a date (pasted, kept with an error) is shown as is.
 */
export function dateCellTemplate(prop: string): any {
  return (h: any, props: any) => {
    const value = props.model[prop];
    const iso = dateInputValue(value);
    const text = iso ? displayDate(iso) : value === null || value === undefined ? '' : String(value);
    // a calendar on the right says the cell takes a date
    return h('div', { class: 'revo-cell-with-icon' }, [
      h('span', { class: 'revo-cell-text' }, text),
      h(
        'svg',
        { width: '14', height: '14', viewBox: '0 0 20 20', fill: 'none', class: 'revo-cell-icon', 'aria-hidden': 'true' },
        [
          h('rect', { x: '3', y: '4.5', width: '14', height: '12.5', rx: '2', stroke: 'currentColor', 'stroke-width': '1.5' }),
          h('path', { d: 'M3 8.5h14M7 2.5v4M13 2.5v4', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-linecap': 'round' }),
        ],
      ),
    ]);
  };
}

/**
 * Grid editor for date columns: a native date input inside the cell's editor (Enter / double-click), committed
 * with Enter, on picking a date, or when the editor closes. Escape cancels (handled by the grid).
 */
export function createDateEditor(): EditorCtrConstructible {
  return class DateEditor implements EditorBase {
    element: HTMLInputElement | null = null;
    editCell?: EditCell;

    constructor(
      private data: ColumnDataSchemaModel,
      private save: (value: any, preventFocus?: boolean) => void,
    ) {}

    getValue(): string | null {
      return this.element?.value || null;
    }

    componentDidRender(): void {
      const input = this.element;
      if (!input || document.activeElement === input) return;
      input.focus();
      // open the calendar straight away; browsers without showPicker (or without a user gesture) just keep the input
      try {
        input.showPicker();
      } catch {
        /* typing into the input still works */
      }
    }

    render(h: HyperFunc<VNode>): VNode {
      return h('input', {
        type: 'date',
        class: 'revo-date-editor-input',
        value: dateInputValue(this.data.model?.[this.data.prop]),
        min: '1900-01-01',
        max: '2100-12-31',
        ref: (el: HTMLInputElement) => (this.element = el),
        onKeyDown: (event: KeyboardEvent) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.save(this.getValue());
          }
        },
        // picking from the calendar completes the date in one step
        onChange: () => this.save(this.getValue(), true),
      });
    }
  };
}

/** Stored forms of a boolean; any other text is pasted input kept for the user to fix. */
const BOOLEAN_TEXT = /^(true|false|1|0)?$/i;

/** A checkbox; text that is not a boolean (pasted, kept for the user to fix) is shown as text instead. */
export function booleanCellTemplate(
  prop: string,
  onChange?: (model: any) => void,
  disabled?: (model: any) => boolean,
): any {
  return (h: any, props: any) => {
    const value = props.model[prop];
    if (typeof value === 'string' && !BOOLEAN_TEXT.test(value.trim())) {
      return h('span', { class: 'revo-cell-text' }, value);
    }
    return h('input', {
      type: 'checkbox',
      checked: typeof value === 'string' ? /^(true|1)$/i.test(value.trim()) : !!value,
      disabled: !!disabled?.(props.model),
      onMouseDown: (event: MouseEvent) => event.stopPropagation(),
      onChange: (event: Event) => {
        props.model[prop] = (event.target as HTMLInputElement).checked;
        onChange?.(props.model);
      },
    });
  };
}

/** English and Indonesian month names (and Indonesian abbreviations that are not a prefix), January first. */
const MONTH_NAMES = [
  ['january', 'januari'],
  ['february', 'februari', 'pebruari'],
  ['march', 'maret'],
  ['april'],
  ['may', 'mei'],
  ['june', 'juni'],
  ['july', 'juli'],
  ['august', 'agustus', 'agt', 'ags'],
  ['september'],
  ['october', 'oktober'],
  ['november', 'nopember'],
  ['december', 'desember'],
];

/** Month (1-12) of a name, or of an abbreviation (3+ letters) that starts the names of a single month. */
function monthOf(name: string): number | null {
  const text = name.toLowerCase().replace(/\.$/, '');
  const exact = MONTH_NAMES.findIndex((names) => names.includes(text));
  if (exact >= 0) return exact + 1;
  if (text.length < 3) return null;
  const months = MONTH_NAMES.flatMap((names, index) => (names.some((full) => full.startsWith(text)) ? [index + 1] : []));
  return months.length === 1 ? months[0] : null;
}

function isoDate(year: number, month: number, day: number): string | null {
  if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Typed / pasted date text as `yyyy-mm-dd`, or null when it is not a real date. Accepts `yyyy-mm-dd` (a time
 * after it is ignored), day-first `dd/mm/yyyy`, `dd-mm-yyyy`, `dd.mm.yyyy` (as written in Indonesia, never
 * month-first), and `d Month yyyy` / `Month d, yyyy` with English or Indonesian month names.
 */
export function parseDateText(value: unknown): string | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : isoDate(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }
  const text = String(value ?? '').trim();
  let match = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T\s].*)?$/.exec(text);
  if (match) return isoDate(+match[1], +match[2], +match[3]);
  match = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(text);
  if (match) return isoDate(+match[3], +match[2], +match[1]);
  match = /^(\d{1,2})[\s-]+([\p{L}]+\.?)[\s-]+(\d{4})$/u.exec(text);
  if (match) {
    const month = monthOf(match[2]);
    return month ? isoDate(+match[3], month, +match[1]) : null;
  }
  match = /^([\p{L}]+\.?)\s+(\d{1,2}),?\s+(\d{4})$/u.exec(text);
  if (match) {
    const month = monthOf(match[1]);
    return month ? isoDate(+match[3], month, +match[2]) : null;
  }
  return null;
}

export function seedNewRows(event: CustomEvent<{ newRows: { index: number; data: any }[] }>, createRow: () => any): void {
  event.detail.newRows.forEach((row) => (row.data = { ...createRow(), ...row.data }));
}

export interface MultiSelectEditorConfig {
  /** Options available for the row being edited. */
  source: (model: any) => any[];
  labelKey: string;
  valueKey: string;
  /** A "Select all" row above the options (default true). */
  selectAll?: boolean;
  /** Translated panel texts; read when the editor opens so a language change applies. */
  texts?: () => { empty: string; done: string; selectAll?: string; clearAll?: string };
}

export function createMultiSelectEditor(config: MultiSelectEditorConfig): EditorCtrConstructible {
  return class MultiSelectEditor implements EditorBase {
    element: HTMLElement | null = null;
    editCell?: EditCell;
    private panel: HTMLElement | null = null;
    private selected: string[];
    private readonly initial: any[];

    constructor(
      private data: ColumnDataSchemaModel,
      private save: (value: any, preventFocus?: boolean) => void,
    ) {
      const current = data.model?.[data.prop];
      this.initial = Array.isArray(current) ? current : [];
      this.selected = this.initial.map((value: any) => String(value));
    }

    getValue(): any[] {
      const options = config.source(this.data.model);
      const known = new Set(options.map((option) => String(option[config.valueKey])));
      // keep option order and original value types; values the list does not show (not loaded yet, or out of
      // scope) cannot be unticked here, so they are kept rather than dropped by closing the editor
      return [
        ...options.map((option) => option[config.valueKey]).filter((value) => this.selected.includes(String(value))),
        ...this.initial.filter((value) => !known.has(String(value))),
      ];
    }

    componentDidRender(): void {
      if (!this.element || this.panel) return;
      this.panel = this.buildPanel();
      // inside a CDK dialog (browser top layer) the panel must live in the dialog's popover to be visible
      (this.element.closest('[popover]') ?? document.body).appendChild(this.panel);
      this.positionPanel();
    }

    disconnectedCallback(): void {
      this.panel?.remove();
      this.panel = null;
    }

    render(h: HyperFunc<VNode>): VNode {
      return h(
        'div',
        { class: 'revo-multi-select-anchor', ref: (el: HTMLElement) => (this.element = el) },
        this.selectedLabels().join(', '),
      );
    }

    private buildPanel(): HTMLElement {
      const panel = document.createElement('div');
      panel.className = 'revo-multi-select-panel';
      panel.addEventListener('mousedown', (event) => event.stopPropagation());

      const options = config.source(this.data.model);
      const texts = config.texts?.();
      if (!options.length) {
        const empty = document.createElement('div');
        empty.className = 'revo-multi-select-empty';
        empty.textContent = texts?.empty ?? 'No options available';
        panel.appendChild(empty);
      }

      const values = options.map((option) => String(option[config.valueKey]));
      const checkboxes: HTMLInputElement[] = [];
      // "Select all" row, pinned above the options: ticks every option, or unticks them all once all are ticked
      // (indeterminate while only some are)
      const toggleAll = document.createElement('label');
      toggleAll.className = 'revo-multi-select-option revo-multi-select-toggle-all';
      const toggleBox = document.createElement('input');
      toggleBox.type = 'checkbox';
      const toggleText = document.createElement('span');
      toggleAll.append(toggleBox, toggleText);
      const allSelected = (): boolean => values.every((value) => this.selected.includes(value));
      const refresh = (): void => {
        const all = allSelected();
        const some = values.some((value) => this.selected.includes(value));
        toggleBox.checked = all;
        toggleBox.indeterminate = some && !all;
        toggleText.textContent = all ? (texts?.clearAll ?? 'Clear all') : (texts?.selectAll ?? 'Select all');
        checkboxes.forEach((checkbox, i) => (checkbox.checked = this.selected.includes(values[i])));
        if (this.element) this.element.textContent = this.selectedLabels().join(', ');
      };
      toggleBox.addEventListener('change', () => {
        this.selected = allSelected()
          ? this.selected.filter((value) => !values.includes(value))
          : [...new Set([...this.selected, ...values])];
        refresh();
      });
      if (options.length && config.selectAll !== false) panel.appendChild(toggleAll);

      options.forEach((option, i) => {
        const value = values[i];
        const label = document.createElement('label');
        label.className = 'revo-multi-select-option';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.addEventListener('change', () => {
          this.selected = checkbox.checked
            ? [...this.selected, value]
            : this.selected.filter((selectedValue) => selectedValue !== value);
          refresh();
        });
        checkboxes.push(checkbox);

        label.appendChild(checkbox);
        label.appendChild(document.createTextNode(String(option[config.labelKey])));
        panel.appendChild(label);
      });
      refresh();

      const done = document.createElement('button');
      done.type = 'button';
      done.className = 'revo-multi-select-done';
      done.textContent = texts?.done ?? 'Done';
      done.addEventListener('click', () => this.save(this.getValue()));
      panel.appendChild(done);

      return panel;
    }

    private positionPanel(): void {
      if (!this.element || !this.panel) return;
      const rect = this.element.getBoundingClientRect();
      const panelHeight = this.panel.offsetHeight;
      const opensUp = rect.bottom + panelHeight > window.innerHeight && rect.top > panelHeight;
      this.panel.style.left = `${rect.left}px`;
      this.panel.style.minWidth = `${rect.width}px`;
      this.panel.style.top = opensUp ? `${rect.top - panelHeight}px` : `${rect.bottom}px`;
    }

    private selectedLabels(): string[] {
      return config
        .source(this.data.model)
        .filter((option) => this.selected.includes(String(option[config.valueKey])))
        .map((option) => String(option[config.labelKey]));
    }
  };
}

export interface SingleSelectOption {
  value: any;
  label: string;
}

export interface SingleSelectEditorConfig {
  /** Options available for the row being edited. */
  source: (model: any) => SingleSelectOption[];
  /** Text a search has to start with (case-insensitive); the label when not given. */
  searchText?: (option: SingleSelectOption) => string;
  /** Translated "no match" text; read when the editor opens so a language change applies. */
  emptyText?: () => string;
}

/**
 * Single-choice editor with type-to-search. Typing keeps the options whose search text starts with what was typed
 * (case-insensitive, "X%" rather than "%X%") and highlights the first one, so Enter picks it. Arrow keys move the
 * highlight, Enter or a click picks, Escape cancels (handled by the grid). Typing on a selected cell opens the editor
 * with that key already in the search.
 */
export function createSingleSelectEditor(config: SingleSelectEditorConfig): EditorCtrConstructible {
  return class SingleSelectEditor implements EditorBase {
    element: HTMLInputElement | null = null;
    editCell?: EditCell;
    private panel: HTMLElement | null = null;
    private readonly options: SingleSelectOption[];
    private matches: SingleSelectOption[] = [];
    private active = -1;
    // closing without searching or moving the highlight keeps the cell as it was
    private touched = false;

    constructor(
      private data: ColumnDataSchemaModel,
      private save: (value: any, preventFocus?: boolean) => void,
    ) {
      this.options = config.source(data.model);
    }

    private get current(): any {
      return this.data.model?.[this.data.prop];
    }

    getValue(): any {
      return this.touched ? (this.matches[this.active]?.value ?? this.current) : this.current;
    }

    componentDidRender(): void {
      const input = this.element;
      if (!input || this.panel) return;
      this.panel = document.createElement('div');
      this.panel.className = 'revo-single-select-panel';
      // keep focus in the search input while clicking an option
      this.panel.addEventListener('mousedown', (event) => event.preventDefault());
      (input.closest('[popover]') ?? document.body).appendChild(this.panel);
      this.touched = input.value !== '';
      this.filter(input.value);
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    }

    disconnectedCallback(): void {
      this.panel?.remove();
      this.panel = null;
    }

    render(h: HyperFunc<VNode>): VNode {
      const typed = typeof this.editCell?.val === 'string' ? this.editCell.val : '';
      const currentLabel = this.options.find((option) => option.value === this.current)?.label ?? this.current ?? '';
      return h('input', {
        type: 'text',
        class: 'revo-single-select-input',
        value: typed,
        placeholder: String(currentLabel),
        ref: (el: HTMLInputElement) => (this.element = el),
        onInput: (event: Event) => {
          this.touched = true;
          this.filter((event.target as HTMLInputElement).value);
        },
        onKeyDown: (event: KeyboardEvent) => this.onKeyDown(event),
      });
    }

    private onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        if (!this.matches.length) return;
        this.touched = true;
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.active = Math.min(this.matches.length - 1, Math.max(0, this.active + step));
        this.renderPanel();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this.save(this.matches[this.active]?.value ?? this.current);
      }
    }

    private filter(text: string): void {
      const query = text.trim().toLowerCase();
      const searchText = config.searchText ?? ((option: SingleSelectOption) => option.label);
      this.matches = query
        ? this.options.filter((option) => String(searchText(option) ?? '').trim().toLowerCase().startsWith(query))
        : this.options;
      // a search highlights its first match; no search highlights the current value
      this.active = query ? (this.matches.length ? 0 : -1) : this.matches.findIndex((option) => option.value === this.current);
      this.renderPanel();
    }

    private renderPanel(): void {
      const panel = this.panel;
      if (!panel) return;
      panel.replaceChildren();
      if (!this.matches.length) {
        const empty = document.createElement('div');
        empty.className = 'revo-single-select-empty';
        empty.textContent = config.emptyText?.() ?? 'No match';
        panel.appendChild(empty);
      }
      this.matches.forEach((option, index) => {
        const item = document.createElement('div');
        item.className = 'revo-single-select-option';
        item.classList.toggle('is-active', index === this.active);
        item.textContent = option.label;
        item.addEventListener('click', () => this.save(option.value));
        panel.appendChild(item);
        if (index === this.active) {
          requestAnimationFrame(() => item.scrollIntoView({ block: 'nearest' }));
        }
      });
      this.positionPanel();
    }

    private positionPanel(): void {
      if (!this.element || !this.panel) return;
      const rect = this.element.getBoundingClientRect();
      const panelHeight = this.panel.offsetHeight;
      const opensUp = rect.bottom + panelHeight > window.innerHeight && rect.top > panelHeight;
      this.panel.style.left = `${rect.left}px`;
      this.panel.style.minWidth = `${rect.width}px`;
      this.panel.style.top = opensUp ? `${rect.top - panelHeight}px` : `${rect.bottom}px`;
    }
  };
}

/** `yyyy-mm-dd` for the date input; '' when the value is empty or not a date. */
function dateInputValue(value: any): string {
  if (!value) return '';
  // local date parts: toISOString() would shift the day east of UTC
  if (value instanceof Date) return parseDateText(value) ?? '';
  const match = /^(\d{4}-\d{2}-\d{2})(?:[T\s].*)?$/.exec(String(value).trim());
  return match ? match[1] : '';
}

const nextFrame = (): Promise<void> => new Promise((resolve) => requestAnimationFrame(() => resolve()));

export function firstEditableColumn(columns: any[], row: any): number {
  const index = columns.findIndex((column) =>
    typeof column.readonly === 'function' ? !column.readonly({ model: row, prop: column.prop }) : !column.readonly,
  );
  return Math.max(0, index);
}

export async function focusRevoGridRow(grid: any, row: any, column = 0): Promise<void> {
  if (!grid) return;
  (document.activeElement as HTMLElement | null)?.blur?.();
  let rowIndex = -1;
  for (let frame = 0; frame < 30 && rowIndex < 0; frame++) {
    await nextFrame();
    const source: any[] = (await grid.getVisibleSource?.().catch(() => [])) ?? [];
    rowIndex = source.indexOf(row);
  }
  if (rowIndex < 0) return;
  await nextFrame();
  await nextFrame();
  grid.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  await grid.scrollToRow(rowIndex).catch(() => undefined);
  await grid.setCellsFocus({ x: column, y: rowIndex }, { x: column, y: rowIndex }).catch(() => undefined);
}

export async function syncRevoGridSource<T>(grid: any, source: T[]): Promise<T[]> {
  const rows: T[] | undefined = await grid?.getSource?.().catch(() => undefined);
  return rows && rows.length !== source.length ? rows : source;
}

export function commitRevoGridEdit(grid: any): void {
  const editor = grid?.shadowRoot?.querySelector('revogr-edit') ?? grid?.querySelector?.('revogr-edit');
  editor?.onAutoSave?.();
  grid?.clearFocus?.();
}

export const isEmptyValue = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === '';

export function withValidationStyles(
  columns: any[],
  required: string[],
  invalid: (model: any, prop: string) => boolean,
): any[] {
  return columns.map((column) => ({
    ...column,
    ...(required.includes(column.prop) ? { columnProperties: () => ({ class: { 'revo-header-required': true } }) } : {}),
    cellProperties: ({ model, prop }: any) => ({ class: { 'revo-cell-invalid': invalid(model, String(prop)) } }),
  }));
}

const ROW_ACTION_ICONS = {
  delete: 'M7.5 3h5M3.5 5.5h13M5 5.5l.7 10.2c.06.85.76 1.5 1.6 1.5h5.4c.84 0 1.54-.65 1.6-1.5L15 5.5M8.5 8.5v5.5M11.5 8.5v5.5',
  undo: 'M7.5 5 4 8.5 7.5 12M4.5 8.5h7.25a4.25 4.25 0 0 1 0 8.5H9',
};

export function rowActionButton(h: any, action: 'delete' | 'undo', label: string, onClick: () => void): VNode {
  return h(
    'button',
    {
      type: 'button',
      class: { 'revo-row-icon-btn': true, 'revo-row-icon-btn-undo': action === 'undo' },
      title: label,
      'aria-label': label,
      onMouseDown: (event: MouseEvent) => event.stopPropagation(),
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
        onClick();
      },
    },
    h(
      'svg',
      { width: '16', height: '16', viewBox: '0 0 20 20', fill: 'none', 'aria-hidden': 'true' },
      h('path', {
        d: ROW_ACTION_ICONS[action],
        stroke: 'currentColor',
        'stroke-width': '1.5',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
      }),
    ),
  );
}
